import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { C } from "../../constants/theme";
import Logo from "../ui/Logo";
import { useSiteContent } from "../../context/SiteContentContext";

const LINKS = [
  { label: "Home",        path: "/" },
  { label: "About",       path: "/about/story", children: [
      { label: "Our Story",        path: "/about/story" },
      { label: "Meet Our Founder", path: "/about" },
      { label: "Meet Our Staff",   path: "/about/staff" },
    ] },
  { label: "Programs",    path: "/programs", children: [
      { label: "All Programmes",        path: "/programs" },
      { label: "Preschool",             path: "/programs/preschool" },
      { label: "Quran for Kids",        path: "/programs/quran-for-kids" },
      { label: "Homeschool Companion",  path: "/programs/homeschool-companion" },
    ] },
  { label: "How Mommy-Inclusive?", path: "/for-mothers" },
  { label: "Resources",   path: "/resources", children: [
      { label: "Paid Resources",   path: "/resources" },
      { label: "Free Resources",   path: "/free-resources" },
      { label: "Upcoming Courses", path: "/upcoming" },
    ] },
  { label: "Admissions",  path: "/admissions" },
  { label: "Contact",     path: "/contact" },
];

const Navbar = () => {
  const { content } = useSiteContent();
  const nb = content.navbar || {};
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setMobileDropdown(null);
  }, [location.pathname]);

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      background: scrolled ? "rgba(254,252,246,0.97)" : "transparent",
      backdropFilter: scrolled ? "blur(12px)" : "none",
      boxShadow: scrolled ? "0 2px 20px rgba(27,63,139,0.08)" : "none",
      transition: "all 0.3s ease",
      padding: "10px 5vw",
      display: "flex", alignItems: "center", justifyContent: "space-between",
    }}>
      <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
        <Logo size={34} />
        <div>
          <div style={{ fontFamily: "Fredoka One", fontSize: 14, color: C.navy, lineHeight: 1.1 }}>{nb.brand || "MINI MUSLIMS NEST"}</div>
          <div style={{ fontFamily: "Nunito", fontSize: 9.5, color: C.muted, letterSpacing: "0.08em" }}>{nb.tagline || "PRESCHOOL & AFTERSCHOOL CLUB"}</div>
        </div>
      </Link>

      {/* Desktop */}
      <div className="mmn-nav-desktop" style={{ display: "flex", gap: 2, alignItems: "center" }}>
        {LINKS.map(({ label, path, children }) => (
          <div
            key={path}
            style={{ position: "relative" }}
            onMouseEnter={() => children && setDropdownOpen(path)}
            onMouseLeave={() => children && setDropdownOpen(null)}
          >
            <Link to={path} style={{
              fontFamily: "Nunito", fontWeight: isActive(path) ? 700 : 600,
              fontSize: 13, color: isActive(path) ? C.coral : C.navy,
              textDecoration: "none", padding: "6px 11px", borderRadius: 8,
              background: isActive(path) ? `${C.coral}12` : "transparent",
              transition: "all 0.18s", letterSpacing: "0.01em",
              display: "inline-flex", alignItems: "center", gap: 4,
            }}
              onMouseOver={e => { if (!isActive(path)) e.currentTarget.style.background = `${C.navy}08`; }}
              onMouseOut={e => { if (!isActive(path)) e.currentTarget.style.background = "transparent"; }}
            >{label}{children && <span style={{ fontSize: 9, opacity: 0.6 }}>▾</span>}</Link>

            {children && (
              <div style={{
                position: "absolute", top: "100%", left: 0, paddingTop: 8,
                opacity: dropdownOpen === path ? 1 : 0,
                visibility: dropdownOpen === path ? "visible" : "hidden",
                transform: dropdownOpen === path ? "translateY(0)" : "translateY(-6px)",
                transition: "all 0.18s ease",
              }}>
                <div style={{
                  background: C.white, borderRadius: 12, minWidth: 200,
                  boxShadow: "0 10px 30px rgba(27,63,139,0.15)", overflow: "hidden",
                  border: `1px solid ${C.navy}10`,
                }}>
                  {children.map(c => (
                    <Link key={c.path} to={c.path} style={{
                      display: "block", fontFamily: "Nunito", fontWeight: 600, fontSize: 13,
                      color: location.pathname === c.path ? C.coral : C.navy,
                      textDecoration: "none", padding: "11px 16px",
                      borderBottom: `1px solid ${C.navy}08`,
                    }}
                      onMouseOver={e => { e.currentTarget.style.background = `${C.navy}08`; }}
                      onMouseOut={e => { e.currentTarget.style.background = "transparent"; }}
                    >{c.label}</Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        <Link to="/admissions#interest-form" style={{
          background: C.navy, color: C.white, textDecoration: "none",
          fontFamily: "Nunito", fontWeight: 700, fontSize: 12.5,
          padding: "9px 20px", borderRadius: 100,
          letterSpacing: "0.04em", transition: "all 0.2s", marginLeft: 10,
          boxShadow: "0 4px 14px rgba(27,63,139,0.25)",
        }}
          onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 22px rgba(27,63,139,0.35)"; }}
          onMouseOut={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 14px rgba(27,63,139,0.25)"; }}
        >Enroll Your Child</Link>
      </div>

      {/* Mobile hamburger */}
      <button onClick={() => setMenuOpen(!menuOpen)} className="mmn-nav-mobile" style={{
        background: "none", border: "none", cursor: "pointer",
        display: "none", flexDirection: "column", gap: 5, padding: 4,
      }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            width: 24, height: 2.5, background: C.navy, borderRadius: 4,
            transform: menuOpen
              ? (i === 0 ? "rotate(45deg) translateY(10.5px)" : i === 2 ? "rotate(-45deg) translateY(-10.5px)" : "scaleX(0)")
              : "none",
            transition: "0.2s",
          }} />
        ))}
      </button>

      {menuOpen && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          background: C.cream, padding: "16px 6vw 24px",
          borderTop: `3px solid ${C.yellow}`,
          display: "flex", flexDirection: "column", gap: 2,
          boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
        }}>
          {LINKS.map(({ label, path, children }) => (
            <div key={path}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Link to={path} style={{
                  flex: 1, fontFamily: "Nunito", fontWeight: 700, fontSize: 15,
                  color: isActive(path) ? C.coral : C.navy,
                  textDecoration: "none", padding: "10px 4px",
                }}>{label}</Link>
                {children && (
                  <button
                    onClick={(e) => { e.preventDefault(); setMobileDropdown(mobileDropdown === path ? null : path); }}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: "10px", fontSize: 12, color: C.navy }}
                  >{mobileDropdown === path ? "▴" : "▾"}</button>
                )}
              </div>
              {children && mobileDropdown === path && (
                <div style={{ display: "flex", flexDirection: "column", paddingLeft: 16, marginBottom: 4 }}>
                  {children.map(c => (
                    <Link key={c.path} to={c.path} style={{
                      fontFamily: "Nunito", fontWeight: 600, fontSize: 14,
                      color: location.pathname === c.path ? C.coral : C.muted,
                      textDecoration: "none", padding: "8px 4px",
                    }}>{c.label}</Link>
                  ))}
                </div>
              )}
              <div style={{ borderBottom: `1px solid ${C.navy}10` }} />
            </div>
          ))}
          <Link to="/admissions#interest-form" style={{
            background: C.navy, color: C.white, textDecoration: "none",
            fontFamily: "Nunito", fontWeight: 800, fontSize: 14,
            padding: "13px", borderRadius: 100, textAlign: "center", marginTop: 12,
          }}>Enroll Your Child</Link>
        </div>
      )}

      <style>{`
        @media(max-width:960px){.mmn-nav-desktop{display:none!important}.mmn-nav-mobile{display:flex!important}}
        @media(min-width:961px){.mmn-nav-mobile{display:none!important}}
      `}</style>
    </nav>
  );
};

export default Navbar;
